import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { supabase } from '../../lib/supabase';
import { AlertCircle, Loader2, DollarSign, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ProceedsDistribution = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [stats, setStats] = useState({
    totalTickets: 0,
    ticketsSold: 0,
    totalRevenue: 0,
    platformFee: 0,
    netProceeds: 0
  });
  const [payoutHistory, setPayoutHistory] = useState<any[]>([]);
  const [payoutInProgress, setPayoutInProgress] = useState(false);

  useEffect(() => {
    if (!campaignId) {
      setError('Invalid campaign ID');
      setLoading(false);
      return;
    }
    fetchCampaignDetails();
    fetchPayoutHistory();
  }, [campaignId]);

  const fetchCampaignDetails = async () => {
    try {
      // Fetch campaign details
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select(`
          *,
          organizations (*)
        `)
        .eq('id', campaignId)
        .single();

      if (campaignError) throw campaignError;
      if (!campaignData) throw new Error('Campaign not found');
      
      setCampaign(campaignData);
      setOrganization(campaignData.organizations);

      // Fetch ticket sales data
      const { data: entriesData, error: entriesError } = await supabase
        .from('entries')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('payment_status', 'paid');

      if (entriesError) throw entriesError;

      // Calculate statistics
      const ticketsSold = entriesData ? entriesData.length : 0;
      const totalRevenue = entriesData ? entriesData.reduce((sum, entry) => sum + (entry.total_amount || 0), 0) : 0;
      const platformFeePercentage = campaignData.organizations?.platform_fee_percentage || 10;
      const platformFee = (totalRevenue * platformFeePercentage) / 100;
      const netProceeds = totalRevenue - platformFee;

      setStats({
        totalTickets: campaignData.max_tickets || 0,
        ticketsSold,
        totalRevenue,
        platformFee,
        netProceeds
      });

      setLoading(false);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to load campaign details');
      setLoading(false);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('proceeds_distributions')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPayoutHistory(data || []);
    } catch (error: any) {
      console.error('Error fetching payout history:', error);
      // Don't set the main error state, just log it
    }
  };

  const handlePayoutProceeds = async (data: any) => {
    try {
      setPayoutInProgress(true);
      
      // Record the payout in the database
      const { data: payoutData, error: payoutError } = await supabase
        .from('proceeds_distributions')
        .insert({
          campaign_id: campaignId,
          amount: stats.netProceeds,
          status: 'processing',
          paypal_payout_id: data.orderID
        })
        .select()
        .single();

      if (payoutError) throw payoutError;

      // Capture the payout
      const response = await fetch(`/api/paypal/capture-payout/${data.orderID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: stats.netProceeds,
          campaignId,
          payoutId: payoutData.id
        }),
      });

      const responseData = await response.json();
      if (responseData.error) {
        throw new Error(responseData.error);
      }

      // Update the payout record
      const { error: updateError } = await supabase
        .from('proceeds_distributions')
        .update({
          status: 'completed',
          distributed_at: new Date().toISOString()
        })
        .eq('id', payoutData.id);

      if (updateError) throw updateError;

      // Show success message
      toast.success('Proceeds have been successfully distributed!');
      
      // Refresh payout history
      fetchPayoutHistory();
      setPayoutInProgress(false);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to distribute proceeds');
      setPayoutInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
        <p>Loading campaign details...</p>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-center text-red-600 mb-4">
          <AlertCircle className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">Error</h2>
        <p className="text-gray-600 text-center mb-6">{error}</p>
        <button
          onClick={() => navigate('/admin/campaigns')}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors"
        >
          Return to Campaigns
        </button>
      </div>
    );
  }

  const isPastEndDate = new Date(campaign.end_date) < new Date();
  const hasUnpaidProceeds = stats.netProceeds > 0 && !payoutHistory.some(p => p.status === 'completed');
  const canDistributeProceeds = isPastEndDate && hasUnpaidProceeds;

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Proceeds Distribution</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Campaign Details</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium">{campaign.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Organization:</span>
              <span className="font-medium">{organization?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Start Date:</span>
              <span className="font-medium">{new Date(campaign.start_date).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">End Date:</span>
              <span className="font-medium">{new Date(campaign.end_date).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`font-medium ${isPastEndDate ? 'text-green-600' : 'text-yellow-600'}`}>
                {isPastEndDate ? 'Completed' : 'Active'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Tickets Sold:</span>
              <span className="font-medium">{stats.ticketsSold} / {stats.totalTickets || 'Unlimited'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Revenue:</span>
              <span className="font-medium">${stats.totalRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Platform Fee ({organization?.platform_fee_percentage || 10}%):</span>
              <span className="font-medium">-${stats.platformFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold">
              <span>Net Proceeds:</span>
              <span className="text-green-600">${stats.netProceeds.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {payoutHistory.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Payout History</h3>
          <div className="bg-gray-50 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payoutHistory.map((payout) => (
                  <tr key={payout.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${payout.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        payout.status === 'completed' ? 'bg-green-100 text-green-800' : 
                        payout.status === 'processing' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.paypal_payout_id || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canDistributeProceeds ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <DollarSign className="h-6 w-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Distribute Proceeds</h3>
          </div>
          
          <p className="mb-4 text-gray-600">
            The campaign has ended and there are proceeds available for distribution to the organization.
            Click the button below to initiate the payout process.
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center font-semibold">
              <span>Amount to Distribute:</span>
              <span className="text-green-600">${stats.netProceeds.toFixed(2)}</span>
            </div>
          </div>
          
          {payoutInProgress ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin mr-2 text-purple-600" />
              <span>Processing payout...</span>
            </div>
          ) : (
            <PayPalButtons
              style={{ layout: "horizontal" }}
              createOrder={(data, actions) => {
                if (!actions.order) return Promise.reject('PayPal actions not available');
                return actions.order.create({
                  intent: "CAPTURE",
                  purchase_units: [
                    {
                      amount: {
                        currency_code: "USD",
                        value: stats.netProceeds.toFixed(2),
                      },
                      description: `Proceeds distribution for campaign: ${campaign.name}`
                    },
                  ],
                });
              }}
              onApprove={(data) => handlePayoutProceeds(data)}
              onError={(err) => {
                console.error('PayPal Error:', err);
                setError('Payout failed. Please try again.');
              }}
            />
          )}
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-gray-500 mr-2" />
            <h3 className="text-lg font-semibold">Proceeds Distribution</h3>
          </div>
          
          {!isPastEndDate ? (
            <p className="text-gray-600">
              Proceeds can only be distributed after the campaign has ended on {new Date(campaign.end_date).toLocaleDateString()}.
            </p>
          ) : stats.netProceeds <= 0 ? (
            <p className="text-gray-600">
              There are no proceeds available for distribution at this time.
            </p>
          ) : (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-5 w-5 mr-2" />
              <p>Proceeds have been successfully distributed.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProceedsDistribution;
